import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Account, DetailRow, DetailRows, withStyles } from 'vopay';
import { BodyText } from 'UI';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useBusinessTypeOptions from '@/hooks/useBusinessTypeOptions';
import useCountryLabels from '@/hooks/useCountryLabels';
import DetailsModule from './DetailsModule';
import { type DetailValue, renderDetailValue } from './detailValue';
import useSwitchDetailRow from './useSwitchDetailRow';
import useFlowOfFundsRow from './useFlowOfFundsRow';
import ContactCard from './ContactCard';
import styles from './AccountDetailsTab.module.scss';

const cn = withStyles(styles);

type AccountDetailsTabProps = {
  /** Undefined while the account loads, null when the request failed. */
  account: Account | null | undefined;
  /** The account's own bearer, for the document routes the Flow of Funds row calls. */
  accountToken: string;
};

/**
 * The Details tab (spec 5.6.1): the business profile, contact details and address captured at
 * account creation, the platform usage and entitlements, and the four contact cards.
 *
 * Industry and its sub-category are backend-resolved names with no frontend vocabulary to localize
 * against, so they render as sent. The design's Data Aggregator and Notes rows have no source field,
 * so neither is rendered.
 */
export default function AccountDetailsTab({ account, accountToken }: AccountDetailsTabProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.DetailsTab' });
  const { t: tRoot } = useTranslation();
  const { resolveCountryLabel } = useCountryLabels();
  const businessTypeLabels = useBusinessTypeOptions();
  const switchRow = useSwitchDetailRow();
  const flowOfFundsRow = useFlowOfFundsRow(accountToken);

  const {
    response: { accountId: loggedInAccountId },
  } = usePartnerData();

  const field = (pick: (account: Account) => string | undefined): DetailValue =>
    account === undefined ? undefined : account === null ? null : (pick(account) ?? '');

  // A flag the backend leaves out (a build without ID-13271) reads as off, not as still loading.
  const flag = (pick: (account: Account) => boolean | undefined): boolean | null | undefined =>
    account === undefined ? undefined : account === null ? null : (pick(account) ?? false);

  // Loading, failed, missing, or the value itself — the three states every plain row renders through.
  const row = (label: string, value: DetailValue | ReactNode, testId: string): DetailRow => ({
    label,
    value: renderDetailValue(value),
    'data-testid': testId,
  });

  // The same rule as the list's Partner Account Name column: the partner's own accounts say so.
  const partner = field((current) => {
    if (!current.PartnerAccountName) return '';

    return current.PartnerAccountID === loggedInAccountId
      ? `${current.PartnerAccountName} • ${tRoot('Accounts.List.Table.direct', { defaultValue: 'Direct' })}`
      : current.PartnerAccountName;
  });

  const contact = (pick: (account: Account) => Account['PrimaryContact']) =>
    account === undefined ? undefined : account === null ? null : (pick(account) ?? null);

  const accountInfo: DetailRow[] = [
    row(
      t('AccountInfo.accountId', { defaultValue: 'Account ID' }),
      field((current) => current.AccountID),
      'detail-accountId',
    ),
    row(t('AccountInfo.partner', { defaultValue: 'Partner' }), partner, 'detail-partner'),
    row(
      t('AccountInfo.legalBusinessName', { defaultValue: 'Legal Business Name' }),
      field((current) => current.LegalBusinessName),
      'detail-legalBusinessName',
    ),
    // Most accounts trade under their legal name and leave the DBA blank.
    row(
      t('AccountInfo.businessName', { defaultValue: 'Business Name' }),
      field((current) => current.DBAName || current.LegalBusinessName),
      'detail-businessName',
    ),
    row(
      t('AccountInfo.industry', { defaultValue: 'Industry' }),
      field((current) => current.Industry),
      'detail-industry',
    ),
    row(
      t('AccountInfo.industrySubCategory', { defaultValue: 'Industry Sub-Category' }),
      field((current) => current.IndustrySubCategory),
      'detail-industrySubCategory',
    ),
    row(
      t('AccountInfo.businessType', { defaultValue: 'Business Type' }),
      field(
        (current) =>
          current.OrganizationalType && (businessTypeLabels[current.OrganizationalType] ?? current.OrganizationalType),
      ),
      'detail-businessType',
    ),
    ...(flowOfFundsRow ? [flowOfFundsRow] : []),
  ];

  const businessContact: DetailRow[] = [
    row(
      t('BusinessContact.website', { defaultValue: 'Website' }),
      field((current) => current.Website),
      'detail-website',
    ),
    row(
      t('BusinessContact.businessEmail', { defaultValue: 'Business Email' }),
      field((current) => current.Email),
      'detail-businessEmail',
    ),
    row(
      t('BusinessContact.businessPhoneNumber', { defaultValue: 'Business Phone Number' }),
      field((current) => current.Phone),
      'detail-businessPhoneNumber',
    ),
  ];

  const businessAddress: DetailRow[] = [
    row(
      t('BusinessAddress.address', { defaultValue: 'Address' }),
      field((current) => current.Address),
      'detail-address',
    ),
    row(
      t('BusinessAddress.unit', { defaultValue: 'Unit' }),
      field((current) => current.Address2),
      'detail-unit',
    ),
    row(
      t('BusinessAddress.city', { defaultValue: 'City' }),
      field((current) => current.City),
      'detail-city',
    ),
    row(
      t('BusinessAddress.provinceState', { defaultValue: 'Province/State' }),
      field((current) => current.Province),
      'detail-province',
    ),
    row(
      t('BusinessAddress.country', { defaultValue: 'Country' }),
      field((current) => current.Country && resolveCountryLabel(current.Country)),
      'detail-country',
    ),
    row(
      t('BusinessAddress.postalCode', { defaultValue: 'Postal Code/Zip Code' }),
      field((current) => current.PostalCode),
      'detail-postalCode',
    ),
  ];

  const platformUsage: DetailRow[] = [
    row(
      t('PlatformUsage.platformUsage', { defaultValue: 'Platform Usage' }),
      field((current) => current.ProductDescription),
      'detail-platformUsage',
    ),
    switchRow(
      t('PlatformUsage.clientAccounts', { defaultValue: 'Client Accounts' }),
      flag((current) => current.ClientAccountsEnabled),
      'detail-clientAccounts',
    ),
    switchRow(
      t('PlatformUsage.subAccounts', { defaultValue: 'Sub-Accounts' }),
      flag((current) => current.SubAccountsEnabled),
      'detail-subAccounts',
    ),
  ];

  return (
    <div className={cn('tab')} data-testid="accountDetailsTab">
      <div className={cn('modules')}>
        <DetailsModule title={t('AccountInfo.title', { defaultValue: 'Account Info' })} data-testid="accountInfo">
          <DetailRows size="sm" className={cn('rows')} details={accountInfo} />
        </DetailsModule>

        <DetailsModule
          title={t('BusinessContact.title', { defaultValue: 'Business Contact Information' })}
          data-testid="businessContact"
        >
          <DetailRows size="sm" className={cn('rows')} details={businessContact} />
        </DetailsModule>

        <DetailsModule
          title={t('BusinessAddress.title', { defaultValue: 'Business Address' })}
          data-testid="businessAddress"
        >
          <DetailRows size="sm" className={cn('rows')} details={businessAddress} />
        </DetailsModule>

        <DetailsModule
          title={t('PlatformUsage.title', { defaultValue: 'Platform & Account Usage' })}
          data-testid="platformUsage"
        >
          <DetailRows size="sm" className={cn('rows')} details={platformUsage} />
        </DetailsModule>
      </div>

      <section
        className={cn('contacts')}
        aria-label={t('Contacts.title', { defaultValue: 'Contact Information' })}
        data-testid="accountContacts"
      >
        {/* Figma: Body/13/Medium in Text/Secondary75 */}
        <BodyText size="sm" weight="md" className={cn('contactsTitle')}>
          {t('Contacts.title', { defaultValue: 'Contact Information' })}
        </BodyText>

        <ContactCard role="primary" contact={contact((current) => current.PrimaryContact)} />
        <ContactCard role="billing" contact={contact((current) => current.BillingContact)} />
        <ContactCard role="technical" contact={contact((current) => current.TechnicalContact)} />
        <ContactCard role="support" contact={contact((current) => current.SupportContact)} />
      </section>
    </div>
  );
}
